/**
 * PER-VERTEX CLOTH — position-based dynamics on the garment mesh itself.
 *
 * The shipped path (character/cloth.ts) simulates a CAGE of bone chains and skins the mesh to
 * it. That is the cheap approximation, and it has two structural limits we measured on the
 * drifter cloak: folds can only be as fine as the cage (4 columns → visible vertical banding,
 * and raising the cage to 6–7 columns destabilised the tuned solver: snap-turn jitter 106 →
 * 176 → 228, clip 0.8 → 5.1 → 6.0 cm), and the mesh can pass through anything the cage's
 * capsules do not cover. This module simulates the actual vertices instead, so folds emerge
 * from the constraint solve and collision is enforced per vertex.
 *
 * Model — XPBD-flavoured PBD, the Müller-style formulation every engine cloth uses:
 *   predict  x* = x + v·h + g·h²      (h = substep)
 *   solve    distance constraints along mesh edges (structural)
 *            distance constraints across adjacent-triangle diagonals (bending)
 *            capsule collision, per particle, slackened to the AUTHORED bind drape
 *            pinned particles are infinite-mass and driven from outside (the skinned seam)
 *   update   v = (x - x_prev)/h
 * Small substeps with few iterations beat big steps with many — that is what keeps it stable
 * without the heavy damping that makes bone-chain cloth read as cardboard.
 *
 * SPACE: world/render space, like cloth.ts, with the same co-rotating-frame handling. A planet
 * spins and the floating origin rebases, so each step the frame's motion is applied to BOTH the
 * positions and the previous positions — that carries the cloth rigidly with the planet while
 * leaving motion RELATIVE to the frame (which is what makes it trail) intact. Without it the
 * spin carry reads as a permanent gale.
 *
 * ⚠ The caller must refresh the frame's matrixWorld before stepping — the renderer only does it
 * at draw time, so reading it here would be one render stale. avatar.ts already does this for
 * the bone-chain sim; the same call site serves both.
 */
import * as THREE from "three";

/** Max substep length (s). Verlet/PBD is only stable at a bounded step; frames longer than
 *  this integrate in several equal substeps, which is what makes it frame-rate independent. */
const H_MAX = 1 / 120;
/** Max substeps per frame — a hitch simply advances less rather than exploding. */
const MAX_SUBSTEPS = 8;
/** Constraint relaxation passes per substep. */
const ITERS = 2;
/** Structural / bending stiffness, 0..1 per pass. Bending stays soft: it resists creasing
 *  without turning the sheet into a board. */
const K_STRUCT = 1.0;
const K_BEND = 0.12;
/** Per-second velocity damping. */
const DAMPING = 2.2;
/** Gravity (m/s²). Matches the bone-chain sim's tuned value rather than 9.81 — cloth reads
 *  better slightly heavy at this scale. */
const GRAVITY = 10;
/** Air drag toward the ambient wind (per second). */
const AIR_RATE = 6;
/** A root jump beyond this is a teleport / origin rebase, not motion: translate, don't react. */
const SNAP_DIST = 5;
/** Cap on how fast the cloth reacts to root motion (m/s) — above this the excess is carried
 *  rigidly, so dives can't yank the sheet inside out. */
const MAX_REACT_SPEED = 24;
/** Max stretch of a structural edge before it is hard-clamped (runaway backstop). */
const MAX_STRETCH = 1.6;
/** SHAPE RETENTION toward the authored drape, per substep. Gravity and constraints alone give a
 *  sheet that flails off the shoulders whenever the torso swings — real garments are held by
 *  their own stiffness and by resting against the wearer. The bone-chain sim solves this the
 *  same way (its `shape` / REST_SHAPE pair); without it the cape streams sideways at a run and
 *  leaves the back bare. Rises toward K_SHAPE_REST as the wearer settles, so a stood-still cape
 *  hangs calm while a moving one still flows. */
const K_SHAPE_MOVE = 0.02;
const K_SHAPE_REST = 0.22;
/** Apparent-motion band (m/s) the shape factor ramps across. */
const REST_LO = 0.35;
const REST_HI = 2.2;

export interface PbdCapsule {
  a: THREE.Bone | THREE.Object3D;
  b: THREE.Bone | THREE.Object3D;
  r: number;
}

/** A refittable triangle-surface collider (clothBvh.ts implements this over a SkinnedMesh). */
export interface ClothMeshCollider {
  /** Closest-surface point within maxDist of p; fills cp + outward normal n; false = none. */
  contact(p: THREE.Vector3, maxDist: number, cp: THREE.Vector3, n: THREE.Vector3): boolean;
}

/** Mesh contacts are GENERATED once per frame (the BVH query is the expensive part) and
 *  enforced per substep — the standard PBD contact pattern. This is how far from the surface a
 *  particle still gets a contact plane; it bounds how far one can travel in a frame and still
 *  be caught. (During capped-react dives the cloth streams away from the body anyway.) */
const CONTACT_REACH = 0.12;

const _a = new THREE.Vector3();
const _b = new THREE.Vector3();
const _ab = new THREE.Vector3();
const _p = new THREE.Vector3();
const _d = new THREE.Vector3();
const _fm = new THREE.Matrix4();
const _fmInv = new THREE.Matrix4();
const _delta = new THREE.Matrix4();
const _v = new THREE.Vector3();
const _ccp = new THREE.Vector3();
const _cn = new THREE.Vector3();

/** Closest point on segment ab to p, written into out; returns the parameter t. */
function segClosest(p: THREE.Vector3, a: THREE.Vector3, b: THREE.Vector3, out: THREE.Vector3): number {
  _ab.subVectors(b, a);
  const denom = _ab.lengthSq();
  const t = denom > 1e-12 ? THREE.MathUtils.clamp(_p.subVectors(p, a).dot(_ab) / denom, 0, 1) : 0;
  out.copy(a).addScaledVector(_ab, t);
  return t;
}

export class PbdCloth {
  /** Particle state, world space, flat xyz. */
  readonly pos: Float32Array;
  private readonly prev: Float32Array;
  /** 0 = pinned (driven from outside), 1 = free. */
  private readonly free: Float32Array;
  readonly count: number;

  /** Structural + bending constraints as flat [i, j] pairs with rest lengths. */
  private readonly eI: Uint32Array;
  private readonly eJ: Uint32Array;
  private readonly eRest: Float32Array;
  private readonly bI: Uint32Array;
  private readonly bJ: Uint32Array;
  private readonly bRest: Float32Array;

  /** Per (particle, capsule) slack: how deep inside each capsule the AUTHORED drape already
   *  sits. The effective radius for that pair is r − slack, so a cape modelled resting ON the
   *  shoulder is not popped off it the first frame — the same trick the bone-chain sim uses. */
  private slack: Float32Array = new Float32Array(0);
  private caps: PbdCapsule[] = [];
  /** Mesh colliders + per-particle effective margin (bind-slackened, same idea as capsules),
   *  and the per-frame contact planes (cp.xyz, n.xyz per particle per collider). */
  private meshCols: { col: ClothMeshCollider; margin: number }[] = [];
  private meshEff: Float32Array = new Float32Array(0);
  private contacts: Float32Array = new Float32Array(0);
  private contactHas: Uint8Array = new Uint8Array(0);
  /** Contact-generation cost accumulators (ms) — read via Avatar.pbdClothStats(). */
  contactMs = 0;
  contactN = 0;

  private lastFrame: THREE.Matrix4 | null = null;
  private started = false;
  /** Smoothed apparent speed of the pinned ring (m/s) — drives shape retention. */
  private motion = 0;

  constructor(
    /** Bind positions, world space, flat xyz. */
    bind: Float32Array,
    /** Unique mesh edges as flat [i, j] pairs. */
    edges: Uint32Array,
    /** Bending pairs (opposite corners of adjacent triangles) as flat [i, j]. */
    bends: Uint32Array,
    /** Particle indices that are driven from outside (the seam onto the skinned garment). */
    pinned: Iterable<number>,
  ) {
    this.count = bind.length / 3;
    this.pos = Float32Array.from(bind);
    this.prev = Float32Array.from(bind);
    this.free = new Float32Array(this.count).fill(1);
    for (const i of pinned) this.free[i] = 0;

    const n = edges.length / 2;
    this.eI = new Uint32Array(n); this.eJ = new Uint32Array(n); this.eRest = new Float32Array(n);
    for (let k = 0; k < n; k++) {
      const i = edges[k * 2], j = edges[k * 2 + 1];
      this.eI[k] = i; this.eJ[k] = j;
      this.eRest[k] = Math.hypot(bind[i * 3] - bind[j * 3], bind[i * 3 + 1] - bind[j * 3 + 1], bind[i * 3 + 2] - bind[j * 3 + 2]);
    }
    const m = bends.length / 2;
    this.bI = new Uint32Array(m); this.bJ = new Uint32Array(m); this.bRest = new Float32Array(m);
    for (let k = 0; k < m; k++) {
      const i = bends[k * 2], j = bends[k * 2 + 1];
      this.bI[k] = i; this.bJ[k] = j;
      this.bRest[k] = Math.hypot(bind[i * 3] - bind[j * 3], bind[i * 3 + 1] - bind[j * 3 + 1], bind[i * 3 + 2] - bind[j * 3 + 2]);
    }
  }

  /** Bind the collider set and measure per-particle slack against the CURRENT (bind) pose. */
  setCapsules(caps: PbdCapsule[]): void {
    this.caps = caps;
    this.slack = new Float32Array(this.count * caps.length);
    caps.forEach((c, ci) => {
      c.a.getWorldPosition(_a); c.b.getWorldPosition(_b);
      for (let i = 0; i < this.count; i++) {
        _p.set(this.pos[i * 3], this.pos[i * 3 + 1], this.pos[i * 3 + 2]);
        segClosest(_p, _a, _b, _d);
        this.slack[i * caps.length + ci] = Math.max(0, c.r - _p.distanceTo(_d));
      }
    });
  }

  /** Bind the triangle-surface colliders. Per-particle margins are slackened against the
   *  CURRENT pose exactly like capsule slack: a cape vert authored closer to the surface than
   *  `margin` keeps its authored clearance instead of being popped out on frame one; verts
   *  authored INSIDE the surface (a few exist) opt out entirely. */
  setMeshColliders(cols: { col: ClothMeshCollider; margin: number }[]): void {
    this.meshCols = cols;
    const nm = cols.length;
    this.meshEff = new Float32Array(this.count * nm);
    this.contacts = new Float32Array(this.count * nm * 6);
    this.contactHas = new Uint8Array(this.count * nm);
    cols.forEach(({ col, margin }, mi) => {
      for (let i = 0; i < this.count; i++) {
        if (!this.free[i]) continue;
        _p.set(this.pos[i * 3], this.pos[i * 3 + 1], this.pos[i * 3 + 2]);
        let eff = margin;
        if (col.contact(_p, 0.3, _ccp, _cn)) {
          const sd = (_p.x - _ccp.x) * _cn.x + (_p.y - _ccp.y) * _cn.y + (_p.z - _ccp.z) * _cn.z;
          eff = Math.min(margin, sd);
        }
        this.meshEff[i * nm + mi] = eff < 0.002 ? 0 : eff;
      }
    });
  }

  /** DEBUG (probes): worst violation of the mesh-surface constraints RIGHT NOW, by fresh BVH
   *  query (not the cached planes) — ground truth for "does the cloth actually stay outside
   *  the real surface". mm; 0 = fully satisfied. */
  debugWorstMeshViolationMM(): number {
    let worst = 0;
    const nm = this.meshCols.length;
    for (let mi = 0; mi < nm; mi++) {
      const { col } = this.meshCols[mi];
      for (let i = 0; i < this.count; i++) {
        const eff = this.meshEff[i * nm + mi];
        if (!this.free[i] || eff <= 0) continue;
        _p.set(this.pos[i * 3], this.pos[i * 3 + 1], this.pos[i * 3 + 2]);
        if (!col.contact(_p, 0.25, _ccp, _cn)) continue;
        const sd = (_p.x - _ccp.x) * _cn.x + (_p.y - _ccp.y) * _cn.y + (_p.z - _ccp.z) * _cn.z;
        if (eff - sd > worst) worst = eff - sd;
      }
    }
    return worst * 1000;
  }

  /** Drive a pinned particle (the skinned seam ring). */
  setPinnedPosition(i: number, p: THREE.Vector3): void {
    this.pos[i * 3] = p.x; this.pos[i * 3 + 1] = p.y; this.pos[i * 3 + 2] = p.z;
  }

  /** Re-seed to the current pinned/bind configuration — call on spawn, teleport, LOD-in, so the
   *  sheet doesn't carry a stale drape and thrash its way back. */
  reseed(rest: Float32Array): void {
    this.pos.set(rest);
    this.prev.set(rest);
    this.started = false;
    this.lastFrame = null;
    this.contactHas.fill(0);
  }

  /**
   * Advance. `down` is the world-space gravity direction (−up), `frame` the co-rotating
   * reference (the foot planet's group) or null, `wind` ambient air velocity or null,
   * `waterK` 0..1 submersion (buoyant + heavily damped when swimming).
   */
  /**
   * Apply the co-rotating frame's motion to the WHOLE state (positions and prev), so only
   * motion relative to the frame reacts as cloth motion — a planet's spin carry and floating-
   * origin rebases pass through rigidly instead of reading as a gale.
   *
   * ⚠⚠ MUST run BEFORE the pins are driven to their current skinned positions, and step() no
   * longer does it internally. The first overworld build carried the state inside step() —
   * i.e. AFTER PbdCape.driveRest() had already written the pins at current-world — which
   * advanced the pinned seam by a SECOND helping of the frame's motion every frame. On a
   * planet (surface carry ~2 m/s + rotation) the seam permanently led the body and the free
   * sheet flailed and trailed behind the player. The harness has no frame, so it never saw
   * it. Carrying first also keeps the motion measurement frame-local: prev is carried, pins
   * are then written exact, so the pin jump step() measures is player-relative only — a
   * stood-still player on a spinning planet reads as REST (same contract as cloth.ts).
   */
  beginFrame(frame: THREE.Object3D | null): void {
    const n = this.count;
    if (frame) {
      _fm.copy(frame.matrixWorld);
      if (this.lastFrame) {
        _delta.multiplyMatrices(_fm, _fmInv.copy(this.lastFrame).invert());
        for (let i = 0; i < n; i++) {
          _p.set(this.pos[i * 3], this.pos[i * 3 + 1], this.pos[i * 3 + 2]).applyMatrix4(_delta);
          this.pos[i * 3] = _p.x; this.pos[i * 3 + 1] = _p.y; this.pos[i * 3 + 2] = _p.z;
          _p.set(this.prev[i * 3], this.prev[i * 3 + 1], this.prev[i * 3 + 2]).applyMatrix4(_delta);
          this.prev[i * 3] = _p.x; this.prev[i * 3 + 1] = _p.y; this.prev[i * 3 + 2] = _p.z;
        }
      } else this.lastFrame = new THREE.Matrix4();
      this.lastFrame.copy(_fm);
    } else this.lastFrame = null;
  }

  step(
    dt: number, down: THREE.Vector3, wind: THREE.Vector3 | null,
    waterK: number,
    /** Per-particle authored drape in world space (the garment's own skinned position). Free
     *  particles are eased toward it; null disables shape retention. */
    rest: Float32Array | null = null,
  ): void {
    if (!(dt > 0)) return;
    const n = this.count;

    // ---- teleport / origin-rebase guard, measured on the pinned ring (it is the ground truth)
    let px = 0, py = 0, pz = 0, np = 0;
    for (let i = 0; i < n; i++) {
      if (this.free[i]) continue;
      px += this.pos[i * 3]; py += this.pos[i * 3 + 1]; pz += this.pos[i * 3 + 2]; np++;
    }
    if (np) {
      px /= np; py /= np; pz /= np;
      if (!this.started) {
        // first step: seat every free particle relative to the pins, no reaction
        this.prev.set(this.pos);
        this.started = true;
      } else {
        let qx = 0, qy = 0, qz = 0, nq = 0;
        for (let i = 0; i < n; i++) {
          if (this.free[i]) continue;
          qx += this.prev[i * 3]; qy += this.prev[i * 3 + 1]; qz += this.prev[i * 3 + 2]; nq++;
        }
        qx /= nq; qy /= nq; qz /= nq;
        const jump = Math.hypot(px - qx, py - qy, pz - qz);
        this.motion = jump / Math.max(1e-4, dt);
        const cap = MAX_REACT_SPEED * dt;
        if (jump > SNAP_DIST) {
          // rigid translate: the sheet goes with the body, nothing reacts
          const dx = px - qx, dy = py - qy, dz = pz - qz;
          for (let i = 0; i < n; i++) {
            if (!this.free[i]) continue;
            this.pos[i * 3] += dx; this.pos[i * 3 + 1] += dy; this.pos[i * 3 + 2] += dz;
            this.prev[i * 3] += dx; this.prev[i * 3 + 1] += dy; this.prev[i * 3 + 2] += dz;
          }
        } else if (jump > cap && jump > 1e-6) {
          // carry the excess rigidly so only `cap` worth of motion becomes apparent wind
          const f = (jump - cap) / jump;
          const dx = (px - qx) * f, dy = (py - qy) * f, dz = (pz - qz) * f;
          for (let i = 0; i < n; i++) {
            if (!this.free[i]) continue;
            this.prev[i * 3] += dx; this.prev[i * 3 + 1] += dy; this.prev[i * 3 + 2] += dz;
          }
        }
      }
    }

    // ---- mesh contacts: query the BVH surfaces from current positions, store half-space
    // constraints, enforce them per substep in collide(). Generation is the expensive part;
    // enforcement is a dot product.
    this.genContacts();

    const total = Math.min(dt, MAX_SUBSTEPS * H_MAX);
    const steps = Math.max(1, Math.min(MAX_SUBSTEPS, Math.ceil(total / H_MAX)));
    const h = total / steps;
    // buoyancy cancels most of gravity under water, and drag rises steeply
    const g = GRAVITY * (1 - 0.85 * waterK);
    const damp = Math.exp(-(DAMPING + 8 * waterK) * h);
    const airK = 1 - Math.exp(-(AIR_RATE + 10 * waterK) * h);

    for (let s = 0; s < steps; s++) {
      // ---- predict
      for (let i = 0; i < n; i++) {
        if (!this.free[i]) { // pinned: velocity is whatever the skin did, no integration
          this.prev[i * 3] = this.pos[i * 3];
          this.prev[i * 3 + 1] = this.pos[i * 3 + 1];
          this.prev[i * 3 + 2] = this.pos[i * 3 + 2];
          continue;
        }
        const x = this.pos[i * 3], y = this.pos[i * 3 + 1], z = this.pos[i * 3 + 2];
        let vx = (x - this.prev[i * 3]) * damp;
        let vy = (y - this.prev[i * 3 + 1]) * damp;
        let vz = (z - this.prev[i * 3 + 2]) * damp;
        if (wind) { // drag toward the ambient air
          vx += (wind.x * h - vx) * airK;
          vy += (wind.y * h - vy) * airK;
          vz += (wind.z * h - vz) * airK;
        }
        this.prev[i * 3] = x; this.prev[i * 3 + 1] = y; this.prev[i * 3 + 2] = z;
        this.pos[i * 3] = x + vx + down.x * g * h * h;
        this.pos[i * 3 + 1] = y + vy + down.y * g * h * h;
        this.pos[i * 3 + 2] = z + vz + down.z * g * h * h;
      }

      // ---- shape retention: ease toward the authored drape before solving, so the constraint
      // pass reconciles the two rather than fighting a hard snap
      if (rest) {
        const t = Math.max(0, Math.min(1, (this.motion - REST_LO) / (REST_HI - REST_LO)));
        const restK = K_SHAPE_REST + (K_SHAPE_MOVE - K_SHAPE_REST) * (t * t * (3 - 2 * t));
        for (let i = 0; i < n; i++) {
          if (!this.free[i]) continue;
          this.pos[i * 3] += (rest[i * 3] - this.pos[i * 3]) * restK;
          this.pos[i * 3 + 1] += (rest[i * 3 + 1] - this.pos[i * 3 + 1]) * restK;
          this.pos[i * 3 + 2] += (rest[i * 3 + 2] - this.pos[i * 3 + 2]) * restK;
        }
      }

      // ---- solve
      for (let it = 0; it < ITERS; it++) {
        this.solveDistance(this.eI, this.eJ, this.eRest, K_STRUCT);
        this.solveDistance(this.bI, this.bJ, this.bRest, K_BEND);
      }
      this.collide();
    }
    // ---- end-of-frame contact refresh: the surfaces MOVED during the substeps (the torso
    // sweeps into the cloth at a run), so the frame-start planes are stale by one frame of
    // body motion — measured 15–27 mm of surface violation at run/sprint with the start-only
    // pass. Re-query once and hard-project; the stretch backstop below reconciles edges.
    this.genContacts();
    this.projectContacts();
    // runaway backstop: nothing may exceed MAX_STRETCH on a structural edge
    this.solveDistance(this.eI, this.eJ, this.eRest, 1, MAX_STRETCH);
  }

  /** Query every (free particle, mesh collider) pair for a fresh contact plane. */
  private genContacts(): void {
    const nmc = this.meshCols.length;
    if (!nmc) return;
    const t0 = performance.now();
    for (let mi = 0; mi < nmc; mi++) {
      const { col } = this.meshCols[mi];
      for (let i = 0; i < this.count; i++) {
        const k = i * nmc + mi;
        if (!this.free[i] || this.meshEff[k] <= 0) { this.contactHas[k] = 0; continue; }
        _p.set(this.pos[i * 3], this.pos[i * 3 + 1], this.pos[i * 3 + 2]);
        if (col.contact(_p, CONTACT_REACH, _ccp, _cn)) {
          const o = k * 6;
          this.contacts[o] = _ccp.x; this.contacts[o + 1] = _ccp.y; this.contacts[o + 2] = _ccp.z;
          this.contacts[o + 3] = _cn.x; this.contacts[o + 4] = _cn.y; this.contacts[o + 5] = _cn.z;
          this.contactHas[k] = 1;
        } else this.contactHas[k] = 0;
      }
    }
    this.contactMs += performance.now() - t0;
    this.contactN++;
  }

  /** One hard projection of the cached contact planes (the mesh half of collide()). */
  private projectContacts(): void {
    const nmc = this.meshCols.length;
    for (let mi = 0; mi < nmc; mi++) {
      for (let i = 0; i < this.count; i++) {
        const k = i * nmc + mi;
        if (!this.contactHas[k]) continue;
        const o = k * 6;
        const nx = this.contacts[o + 3], ny = this.contacts[o + 4], nz = this.contacts[o + 5];
        const sd = (this.pos[i * 3] - this.contacts[o]) * nx
          + (this.pos[i * 3 + 1] - this.contacts[o + 1]) * ny
          + (this.pos[i * 3 + 2] - this.contacts[o + 2]) * nz;
        const m = this.meshEff[k];
        if (sd < m) {
          // clamp: a correction this large means the plane is unreliable (crease/boundary) —
          // move a bounded amount and let next frame's fresh query finish the job
          const c = Math.min(m - sd, 0.05);
          this.pos[i * 3] += nx * c; this.pos[i * 3 + 1] += ny * c; this.pos[i * 3 + 2] += nz * c;
        }
      }
    }
  }

  private solveDistance(I: Uint32Array, J: Uint32Array, rest: Float32Array, k: number, onlyBeyond = 0): void {
    const pos = this.pos, free = this.free;
    for (let c = 0; c < I.length; c++) {
      const i = I[c], j = J[c];
      const wi = free[i], wj = free[j];
      const w = wi + wj;
      if (w === 0) continue;
      const dx = pos[j * 3] - pos[i * 3], dy = pos[j * 3 + 1] - pos[i * 3 + 1], dz = pos[j * 3 + 2] - pos[i * 3 + 2];
      const d = Math.hypot(dx, dy, dz);
      if (d < 1e-9) continue;
      const r = rest[c];
      if (onlyBeyond && d < r * onlyBeyond) continue;
      const target = onlyBeyond ? r * onlyBeyond : r;
      const corr = ((d - target) / d) * k / w;
      if (wi) { pos[i * 3] += dx * corr * wi; pos[i * 3 + 1] += dy * corr * wi; pos[i * 3 + 2] += dz * corr * wi; }
      if (wj) { pos[j * 3] -= dx * corr * wj; pos[j * 3 + 1] -= dy * corr * wj; pos[j * 3 + 2] -= dz * corr * wj; }
    }
  }

  /** Push every free particle out of every collider, honouring its authored slack. Mesh
   *  contacts are the planes cached by step()'s generation pass; capsules are live. */
  private collide(): void {
    this.projectContacts();
    const nc = this.caps.length;
    if (!nc) return;
    for (let ci = 0; ci < nc; ci++) {
      const c = this.caps[ci];
      c.a.getWorldPosition(_a); c.b.getWorldPosition(_b);
      for (let i = 0; i < this.count; i++) {
        if (!this.free[i]) continue;
        const r = c.r - this.slack[i * nc + ci];
        if (r < 0.02) continue; // slackened away to nothing — no useful surface left
        _p.set(this.pos[i * 3], this.pos[i * 3 + 1], this.pos[i * 3 + 2]);
        segClosest(_p, _a, _b, _d);
        _v.subVectors(_p, _d);
        const dist = _v.length();
        if (dist >= r) continue;
        if (dist < 1e-6) _v.copy(_p).sub(_a).normalize(); else _v.multiplyScalar(1 / dist);
        this.pos[i * 3] = _d.x + _v.x * r;
        this.pos[i * 3 + 1] = _d.y + _v.y * r;
        this.pos[i * 3 + 2] = _d.z + _v.z * r;
      }
    }
  }
}
